// "Set Up & Go" confirmation logic for the release-center create-release flow.
//
// RAS releases in two beats: first a "Set up <rot> <matchup> <line>" message, then
// ~15-25s later a follow-up that RESOLVES it — GO (bet it as set up), CANCEL (no
// play), or OPPOSITE (bet the other side). The Create Release form opens off the
// "Set up" message and pre-fills the play; these pure helpers let the form (1)
// auto-detect that resolution from the live feed, (2) flip the parsed play to its
// opposite when the follow-up says OPPOSITE, and (3) validate the parsed release,
// separating fields that MUST be present (block submit) from ones that only look
// off (warn, but the admin — who is watching — can submit anyway).
//
// Everything here is pure and framework-free so it can be unit-tested in isolation
// (see setupConfirm.test.ts); the React wiring lives in CreateRelease.tsx.

export type SetupOutcome = 'go' | 'opposite' | 'cancel';

// A feed message, as threaded from the release center (content + timestamp). The
// same shape CreateRelease already passes as `precedingMessages`.
export type FeedMessage = { content?: string; createdAt?: string };

// The leading command keyword of a "Set up" line. Matches "Set up"/"Setup" (the
// only two spellings RAS uses) at the very start so a mid-sentence "set up" in
// prose isn't mistaken for a play.
const SETUP_RE = /^\s*(set\s?up|setup)\b/i;

// Resolution keywords, checked in this order so a two-word phrase wins over a
// bare prefix ("no go" is a CANCEL, not a GO). Anchored at the message start
// because a resolution message leads with its verdict.
const OPPOSITE_RE = /^\s*(opposite|opp|flip|reverse|other\s+side)\b/i;
const CANCEL_RE = /^\s*(cancel|no\s*play|noplay|no\s*go|nogo|pass|scratch|void|dead|off)\b/i;
const GO_RE = /^\s*(go|official|play\s+it|bet\s+it|good\s+to\s+go|it'?s\s+on)\b/i;

// The rotation on a line: the first unsigned 3-6 digit token that isn't part of a
// signed/decimal number. Mirrors the rotation parse in CreateRelease so a "Set up"
// line and its resolution are keyed off the same number.
const ROTATION_RE = /(?<![\d.+-])(\d{3,6})(?![\d.])/;

// Is this message a "Set up" line (the play itself)?
export function isSetupMessage(content?: string): boolean {
  return !!content && SETUP_RE.test(content.trim());
}

// The resolution a message expresses, or null if it isn't a GO/CANCEL/OPPOSITE.
// A "Set up" line is never a resolution (it opens a play, it doesn't close one).
export function detectOutcome(content?: string): SetupOutcome | null {
  if (!content) {
    return null;
  }
  const text = content.trim();
  if (SETUP_RE.test(text)) {
    return null;
  }
  if (OPPOSITE_RE.test(text)) {
    return 'opposite';
  }
  if (CANCEL_RE.test(text)) {
    return 'cancel';
  }
  if (GO_RE.test(text)) {
    return 'go';
  }
  return null;
}

// The rotation token on a line, or null.
function rotationOf(content?: string): string | null {
  if (!content) {
    return null;
  }
  const m = content.replace(SETUP_RE, '').match(ROTATION_RE);
  return m ? m[1] : null;
}

export type ResolvedOutcome = {
  outcome: SetupOutcome;
  // The resolving message's text (so the form can show what it matched on) and
  // how the match was made — an exact rotation match is more trustworthy than a
  // by-proximity guess, which the form can surface.
  message: string;
  matchedBy: 'rotation' | 'proximity';
};

// Resolve THIS set-up's outcome from the feed messages that follow it
// (chronological). Two passes:
//   1) Rotation match — the first following resolution that carries the SAME
//      rotation as the set-up. This is the trustworthy match and wins outright,
//      even across a batch of set-ups that are resolved together afterwards.
//   2) Proximity — failing an explicit rotation match, the first resolution that
//      appears before the NEXT "Set up" line (a new set-up starts a new play, so
//      it bounds this one's resolution). A bare "GO" with no rotation resolves
//      here; a resolution that carries a DIFFERENT rotation is skipped as clearly
//      another play's.
// Returns null while nothing has resolved it yet (the form stays pending).
export function resolveSetupOutcome(
  setup: FeedMessage,
  following: FeedMessage[],
): ResolvedOutcome | null {
  const setupRot = rotationOf(setup.content);

  // Pass 1: explicit rotation match, anywhere below (handles batched resolutions).
  if (setupRot) {
    for (const msg of following) {
      const outcome = detectOutcome(msg.content);
      if (outcome && rotationOf(msg.content) === setupRot) {
        return { outcome, message: (msg.content ?? '').trim(), matchedBy: 'rotation' };
      }
    }
  }

  // Pass 2: first resolution by proximity, bounded by the next set-up line.
  for (const msg of following) {
    const content = (msg.content ?? '').trim();
    if (!content) {
      continue;
    }
    if (isSetupMessage(content)) {
      break; // a new play block starts here; this set-up's resolution should precede it
    }
    const outcome = detectOutcome(content);
    if (!outcome) {
      continue;
    }
    const rot = rotationOf(content);
    // A resolution that names a different rotation belongs to another play.
    if (rot && setupRot && rot !== setupRot) {
      continue;
    }
    return { outcome, message: content, matchedBy: 'proximity' };
  }

  return null;
}

// --- OPPOSITE flip ----------------------------------------------------------

// The subset of release fields an OPPOSITE flip touches.
export type FlippableFields = {
  playType?: string;
  side?: string;
  sideIndicator?: string;
  total?: string;
  totalIndicator?: string;
};

// Negate a spread string, preserving the form's string representation.
// "-3.5" -> "3.5", "+3" -> "-3", "3" -> "-3", "0"/"PK" -> "0" (a pick'em has no
// opposite number). Blank stays blank.
export function negateSpread(side?: string): string | undefined {
  if (side === undefined || side === null || side === '') {
    return side;
  }
  const t = String(side).trim();
  if (t === '0' || /^pk$/i.test(t)) {
    return '0';
  }
  if (t.startsWith('-')) {
    return t.slice(1);
  }
  if (t.startsWith('+')) {
    return `-${t.slice(1)}`;
  }
  return `-${t}`;
}

// Flip a parsed play to the opposite side. Returns only the fields that change:
//   - total: Over <-> Under (the number is unchanged).
//   - side:  the bet flips to the other team, so the side indicator flips
//            (visitor <-> home) and the spread negates. The two team slots are
//            NOT swapped — they are the game's teams; only WHICH side is bet
//            changes. (For a side the "Set up" line names one team, so the
//            opposite side's team is typically blank and the form flags it.)
// A play type without a mirror side (prop/future/prop-group) flips nothing.
export function flipToOpposite(fields: FlippableFields): FlippableFields {
  if (fields.playType === 'total') {
    const flipped =
      fields.totalIndicator === 'over'
        ? 'under'
        : fields.totalIndicator === 'under'
          ? 'over'
          : fields.totalIndicator;
    return { totalIndicator: flipped };
  }
  if (fields.playType === 'side') {
    const out: FlippableFields = { side: negateSpread(fields.side) };
    if (fields.sideIndicator === 'home') {
      out.sideIndicator = 'visitor';
    } else if (fields.sideIndicator === 'visitor') {
      out.sideIndicator = 'home';
    }
    return out;
  }
  return {};
}

// --- Field validation -------------------------------------------------------

export type ReleaseIssueLevel = 'missing' | 'questionable';

export type ReleaseIssue = {
  // The form field the issue attaches to (so the UI can highlight it).
  field: string;
  // Human label for the field, shown in the summary.
  label: string;
  level: ReleaseIssueLevel;
  message: string;
};

// The release field values the validator reads (all strings, as the form holds
// them). Only the fields validation cares about are listed.
export type ReleaseFieldValues = {
  sportId?: string;
  playType?: string;
  rotation?: string;
  visitorId?: string;
  homeId?: string;
  side?: string;
  sideIndicator?: string;
  total?: string;
  totalIndicator?: string;
  gameDate?: string;
  price?: string;
  playText?: string;
};

export type ValidateOptions = {
  // Team slots the auto-matcher filled with LOW confidence (a partial/prefix name
  // match). Flagged as questionable so the admin double-checks, never blocking.
  lowConfidenceTeams?: { visitor?: boolean; home?: boolean };
};

const isBlank = (v?: string) => v === undefined || v === null || String(v).trim() === '';

// Validate a parsed/edited release. MISSING issues (required fields absent) block
// submit; QUESTIONABLE issues (values that parse oddly, or low-confidence team
// matches) only warn — the admin is watching and can submit through them.
export function validateReleaseFields(
  v: ReleaseFieldValues,
  opts: ValidateOptions = {},
): ReleaseIssue[] {
  const issues: ReleaseIssue[] = [];
  const miss = (field: string, label: string, message: string) =>
    issues.push({ field, label, level: 'missing', message });
  const warn = (field: string, label: string, message: string) =>
    issues.push({ field, label, level: 'questionable', message });

  const playType = v.playType;
  const isSideOrTotal = playType === 'side' || playType === 'total';
  const isPropLike = playType === 'prop' || playType === 'prop-group' || playType === 'future';

  // Always-required.
  if (isBlank(v.sportId)) {
    miss('sportId', 'Sport', 'Pick a sport.');
  }
  if (isBlank(playType)) {
    miss('playType', 'Play Type', 'Pick a play type.');
  }
  if (isBlank(v.gameDate)) {
    miss('gameDate', 'Game Date', 'Set the game date.');
  }

  // Rotation is required for the game-based play types (props/futures have none).
  if (isSideOrTotal && isBlank(v.rotation)) {
    miss('rotation', 'Rotation', 'No rotation number.');
  }

  if (playType === 'side') {
    if (isBlank(v.visitorId)) {
      miss('visitorId', 'Visitor Team', 'Select the visitor team.');
    }
    if (isBlank(v.homeId)) {
      miss('homeId', 'Home Team', 'Select the home team.');
    }
    if (isBlank(v.sideIndicator)) {
      miss('sideIndicator', 'Side', 'Choose which side is the play.');
    }
    if (isBlank(v.side)) {
      miss('side', 'Point Spread', 'No spread/line.');
    }
  } else if (playType === 'total') {
    if (isBlank(v.visitorId)) {
      miss('visitorId', 'Visitor Team', 'Select the visitor team.');
    }
    if (isBlank(v.homeId)) {
      miss('homeId', 'Home Team', 'Select the home team.');
    }
    if (isBlank(v.totalIndicator)) {
      miss('totalIndicator', 'Over/Under', 'Choose Over or Under.');
    }
    if (isBlank(v.total)) {
      miss('total', 'Total', 'No total/line.');
    }
  } else if (isPropLike) {
    if (isBlank(v.playText)) {
      miss('playText', 'Play Text', 'Describe the play.');
    }
    if (isBlank(v.homeId)) {
      miss('homeId', 'Team', 'Select the team.');
    }
  }

  // Questionable: values that parse oddly. These never block.
  if (!isBlank(v.total)) {
    const n = Number.parseFloat(String(v.total));
    if (!Number.isFinite(n)) {
      warn('total', 'Total', 'Total is not a number.');
    } else if (n < 20 || n > 260) {
      warn('total', 'Total', `Unusual total (${n}).`);
    }
  }
  if (!isBlank(v.side)) {
    const t = String(v.side).trim();
    if (t !== '0' && !/^pk$/i.test(t)) {
      const n = Number.parseFloat(t);
      if (!Number.isFinite(n)) {
        warn('side', 'Point Spread', 'Spread is not a number.');
      } else if (Math.abs(n) > 30) {
        warn('side', 'Point Spread', `Unusually large spread (${t}).`);
      }
    }
  }
  if (!isBlank(v.price) && String(v.price).trim() !== '-110') {
    const n = Number.parseFloat(String(v.price));
    if (!Number.isFinite(n)) {
      warn('price', 'Price', 'Price is not a number.');
    } else if (Math.abs(n) < 100 || Math.abs(n) > 250) {
      warn('price', 'Price', `Unusual price (${v.price}).`);
    }
  }
  if (!isBlank(v.rotation) && !/^\d{3,6}$/.test(String(v.rotation).trim())) {
    warn('rotation', 'Rotation', 'Rotation should be 3-6 digits.');
  }
  if (opts.lowConfidenceTeams?.visitor && !isBlank(v.visitorId)) {
    warn('visitorId', 'Visitor Team', 'Low-confidence team match — double-check.');
  }
  if (opts.lowConfidenceTeams?.home && !isBlank(v.homeId)) {
    warn('homeId', 'Team', 'Low-confidence team match — double-check.');
  }

  return issues;
}
